import { useContext } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { useDeleteCustomMapGeometryMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../Common/__styles__/Modal";
import { Button } from "../Common/Buttons/Button";
import {
  DeleteConfirmationForm,
  DeleteObjectContent,
} from "../Common/DeleteObjectContent";
import Modal from "../Common/Modal";

interface UseDeleteCustomMapGeometryModalProps {
  geometryId: string;
  customMapId: string;
  objectType: string;
  objectName: string;
  updateMap: () => void;
  onDelete: () => void;
}

const DeleteCustomMapGeometryConfirmation = ({
  geometryId,
  customMapId,
  objectType,
  objectName,
  updateMap,
  onDelete,
  closeModal,
}: UseDeleteCustomMapGeometryModalProps & { closeModal: () => void }) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    formState: { isValid },
    control,
  } = useForm<DeleteConfirmationForm>({
    defaultValues: {
      confirmFileDelete: false,
      confirmDashboardRemoval: false,
      confirmPublicWebsiteRemoval: account?.publicPortal.enabled
        ? false
        : undefined,
    },
  });

  const [deleteGeometry, { loading }] = useDeleteCustomMapGeometryMutation({
    variables: { id: geometryId, customMapId },
    onCompleted: () => {
      track("Deleted custom map geometry", {
        geometryLabel: objectName,
        customMapName: objectType,
      });
      updateMap();
      addSuccessToast(`The ${objectType} was successfully deleted`);
      onDelete();
    },
    onError: () => {
      addErrorToast(
        "There was an error deleting this object, please try again. If the problem persists, email us at support@withforerunner.com",
      );
    },
  });

  return (
    <Container>
      <HeaderSection>
        <h1>Delete {objectType}</h1>
        <h2>{objectName}</h2>
      </HeaderSection>
      <FormSection>
        <ContentSection>
          <DeleteObjectContent objectType={objectType} control={control} />
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button
              variant="secondary"
              size="medium"
              onPress={() => closeModal()}
              title="Cancel"
            />
            <Button
              variant="critical"
              isDisabled={!isValid}
              isPending={loading}
              onPress={() => {
                void deleteGeometry();
              }}
              size="medium"
              title={`Delete ${objectType}`}
            />
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export const useDeleteCustomMapGeometryModal = ({
  geometryId,
  customMapId,
  objectType,
  objectName,
  updateMap,
  onDelete,
}: UseDeleteCustomMapGeometryModalProps) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={() => hideModal()}>
        <DeleteCustomMapGeometryConfirmation
          geometryId={geometryId}
          customMapId={customMapId}
          objectType={objectType}
          objectName={objectName}
          updateMap={updateMap}
          onDelete={() => {
            hideModal();
            onDelete();
          }}
          closeModal={hideModal}
        />
      </Modal>
    ),
    [geometryId, customMapId, objectType, objectName, updateMap, onDelete],
  );

  return [showModal, hideModal] as const;
};
